import React, { useEffect } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import FieldRenderer from './FieldRenderer';

/**
 * RepeatableSection: Card-based repeatable sections (not tables)
 * Used for sections like Travel Itinerary where each item is displayed as a grid of fields.
 *
 * Props:
 * - field: {
 *     name: string,
 *     label: string,
 *     addButtonLabel: string, // e.g., "Add Additional Traveler(s)"
 *     fields: Array<{ name, label, type, etc. }>, // Fields to render in each card
 *     minItems?: number, // Minimum number of items (default 1)
 *   }
 * - formData: object
 * - setFormData: function
 * - dynamicOptions: object
 * - dirtyMap: object
 * - setDirtyMap: function
 * - readOnly: boolean
 */
export default function RepeatableSection(props) {
  const fieldName = props.field.name;
  const items = props.formData?.[fieldName] || [];
  const minItems = props.field.minItems || 1;

  const buildEmptyItem = (placeholder = false) => {
    const item = { _id: `${fieldName}-${Date.now()}-${Math.random()}`, __placeholder: placeholder };
    const initFieldInto = (target, fld) => {
      if (!fld) return;
      if (fld.type === 'group' && Array.isArray(fld.fields)) {
        fld.fields.forEach((gf) => initFieldInto(target, gf));
        return;
      }
      if (fld.type === 'date') {
        if (fld.name) target[fld.name] = null;
        return;
      }
      if (fld.type === 'repeatableSection') {
        const nestedMin = fld.minItems || 1;
        const nestedEmpty = [];
        for (let i = 0; i < nestedMin; i++) {
          const nestedItem = {};
          (fld.fields || []).forEach((nf) => initFieldInto(nestedItem, nf));
          nestedEmpty.push(nestedItem);
        }
        if (fld.name) target[fld.name] = nestedEmpty;
        return;
      }
      if (fld.type === 'expenseRows') {
        if (fld.name) target[fld.name] = [];
        return;
      }
      // default primitives
      if (fld.name) target[fld.name] = '';
    };
    (props.field.fields || []).forEach((f) => initFieldInto(item, f));
    return item;
  };

  const addItem = () => {
    props.setFormData((prev) => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), buildEmptyItem(false)],
    }));
  };

  const deleteItem = (index) => {
    if (items.length <= minItems) return;
    props.setFormData((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index),
    }));
  };

  // Ensure at least minItems exist so users see the first card without clicking "Add"
  useEffect(() => {
    if ((items?.length || 0) >= minItems) return;
    const needed = Math.max(0, minItems - (items?.length || 0));
    if (needed === 0) return;
    const newItems = [...(items || [])];
    for (let i = 0; i < needed; i++) {
      newItems.push(buildEmptyItem(true));
    }
    props.setFormData((prev) => ({
      ...prev,
      [fieldName]: newItems,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minItems, fieldName, /* stabilize on items length only */ items?.length]);

  // Ensure every repeatable item has a stable _id to prevent React key churn
  useEffect(() => {
    if (!Array.isArray(items) || items.length === 0) return;
    const needsIds = items.some((it) => !it || !it._id);
    if (!needsIds) return;
    const withIds = items.map((it) => (it && it._id ? it : { _id: `${fieldName}-${Date.now()}-${Math.random()}`, ...(it || {}) }));
    props.setFormData((prev) => ({ ...prev, [fieldName]: withIds }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // If real data loads after we inserted a placeholder, prune one empty placeholder item (not user-added)
  useEffect(() => {
    if (!Array.isArray(items) || items.length < 2) return;
    const isValEmpty = (f, v) => {
      if (!f) return true;
      if (f.type === 'date') return v == null || v === '';
      if (f.type === 'time') return !v;
      if (f.type === 'autocomplete' || f.type === 'dropdown') return v == null || v === '' || (Array.isArray(v) && v.length === 0);
      if (f.type === 'expenseRows' || f.type === 'repeatableSection') return true; // ignore for emptiness
      return v == null || String(v).trim() === '';
    };
    const isItemEmpty = (it) => {
      return (props.field.fields || []).every((f) => {
        if (!f) return true;
        if (f.type === 'group' && Array.isArray(f.fields)) {
          return f.fields.every((gf) => isValEmpty(gf, it?.[gf.name]));
        }
        if (!f.name) return true; // groups handled; unnamed fields skipped
        return isValEmpty(f, it?.[f.name]);
      });
    };
    const emptyIdx = items.findIndex((it) => it?.__placeholder && isItemEmpty(it));
    const hasNonEmpty = items.some((it) => !isItemEmpty(it));
    if (hasNonEmpty && emptyIdx !== -1) {
      props.setFormData((prev) => ({
        ...prev,
        [fieldName]: items.filter((_, i) => i !== emptyIdx),
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  return (
    <Box sx={{ mb: 1.5 }}>
      {(props.field.label || (!props.readOnly && props.field.addButtonLabel)) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, mb: 0.5, mt: 1.5 }}>
          {props.field.label && (
            <Typography variant="h6" sx={{ fontFamily: 'Arial, sans-serif', fontWeight: 400, mb: 0 }}>
              {props.field.label}
            </Typography>
          )}
          {!props.readOnly && (
            <Button
              size="small"
              variant="text"
              disableRipple
              disableFocusRipple
              startIcon={(
                <AddCircleIcon
                  fontSize="small"
                  sx={{ 
                    verticalAlign: 'middle', 
                    color: '#0092C8'
                  }}
                />
              )}
              onClick={addItem}
              sx={{
                boxShadow: 0,
                textTransform: 'none',
                minWidth: 0,
                p: 0,
                m: 0,
                lineHeight: 1.5,
                alignItems: 'center',
                color: '#0092C8',
                '&:hover': { backgroundColor: 'transparent', color: '#0092C8' },
                '&:focus': { backgroundColor: 'transparent' },
                '&:active': { backgroundColor: 'transparent' },
                '& .MuiButton-startIcon': {
                  mr: 0.5,
                  ml: 0,
                  alignItems: 'center',
                },
              }}
            >
              {props.field.addButtonLabel || 'Add Item'}
            </Button>
          )}
        </Box>
      )}

      {items.map((item, idx) => (
        <Box
          key={item._id || idx}
          sx={{ 
            mb: 1.5, 
            position: 'relative',
            pb: 1,
            borderBottom: idx < items.length - 1 ? (theme) => `1px solid ${theme.palette.divider}` : 'none'
          }}
        >
          {/* Remove per-item floating delete; we render a bottom-left button on the LAST item only */}

          <Box display="flex" flexWrap="wrap" gap={1}>
            {props.field.fields.map((subField, sIdx) => {
              // If this is a nested repeatableSection or expenseRows, render it full-width with visual separation
              if (subField.type === 'repeatableSection' || subField.type === 'expenseRows') {
                return (
                  <Box 
                    key={subField.name} 
                    sx={{ 
                      width: '100%',
                      // For expenseRows, remove the extra top divider/spacing to avoid a visual break
                      mt: subField.type === 'expenseRows' ? 0 : 3,
                      pt: subField.type === 'expenseRows' ? 0 : 2,
                      borderTop: subField.type === 'expenseRows' ? 'none' : (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <FieldRenderer
                      field={subField}
                      formData={item}
                      setFormData={(updater) => {
                        props.setFormData((prev) => {
                          const currItems = Array.isArray(prev[fieldName]) ? [...prev[fieldName]] : [];
                          const baseItem = currItems[idx] || {};
                          const newItem = typeof updater === 'function' ? updater(baseItem) : updater;
                          // Remove placeholder flag when user edits the item
                          if (newItem.__placeholder) {
                            delete newItem.__placeholder;
                          }
                          currItems[idx] = newItem;
                          return { ...prev, [fieldName]: currItems };
                        });
                      }}
                      dynamicOptions={props.dynamicOptions}
                      dirtyMap={props.dirtyMap}
                      setDirtyMap={props.setDirtyMap}
                      readOnly={props.readOnly}
                    />
                  </Box>
                );
              }
              // Support grouped trio inside repeatable section (schema-defined group wrapper)
              if (subField.type === 'group' && Array.isArray(subField.fields)) {
                return (
                  <Box
                    key={subField.name || `group-${idx}-${sIdx}`}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    {subField.fields.map((gField) => (
                      <Box key={gField.name} flex={gField.flex || '1 1 240px'} minWidth={200}>
                        <FieldRenderer
                          field={gField}
                          formData={item}
                          setFormData={(updater) => {
                            props.setFormData((prev) => {
                              const currItems = Array.isArray(prev[fieldName]) ? [...prev[fieldName]] : [];
                              const baseItem = currItems[idx] || {};
                              const newItem = typeof updater === 'function' ? updater(baseItem) : updater;
                              // Remove placeholder flag when user edits the item
                              if (newItem.__placeholder) {
                                delete newItem.__placeholder;
                              }
                              currItems[idx] = newItem;
                              return { ...prev, [fieldName]: currItems };
                            });
                          }}
                          dynamicOptions={props.dynamicOptions}
                          dirtyMap={props.dirtyMap}
                          setDirtyMap={props.setDirtyMap}
                          readOnly={props.readOnly}
                        />
                      </Box>
                    ))}
                  </Box>
                );
              }
              
              // Regular fields use flex layout
              return (
                <Box key={subField.name} flex={subField.flex || '1 1 280px'} minWidth={240}>
                  <FieldRenderer
                    field={subField}
                    formData={item}
                    setFormData={(updater) => {
                      props.setFormData((prev) => {
                        const currItems = Array.isArray(prev[fieldName]) ? [...prev[fieldName]] : [];
                        const baseItem = currItems[idx] || {};
                        const newItem = typeof updater === 'function' ? updater(baseItem) : updater;
                        // Remove placeholder flag when user edits the item
                        if (newItem.__placeholder) {
                          delete newItem.__placeholder;
                        }
                        currItems[idx] = newItem;
                        return { ...prev, [fieldName]: currItems };
                      });
                    }}
                    dynamicOptions={props.dynamicOptions}
                    dirtyMap={props.dirtyMap}
                    setDirtyMap={props.setDirtyMap}
                    readOnly={props.readOnly}
                  />
                </Box>
              );
            })}
          </Box>
          {/* Bottom-left remove button only on last item and only if above minItems */}
          {!props.readOnly && idx === items.length - 1 && items.length > minItems && (
            <Box sx={{ mt: 0.5 }}>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => deleteItem(idx)}
              >
                {props.field.removeButtonLabel || 'Remove Item'}
              </Button>
            </Box>
          )}
        </Box>
      ))}

      {/* Add button moved to header; no secondary button at bottom */}
    </Box>
  );
}
